import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../hooks/useLanguage';
import KPICards from '../components/metrics/KPICards';
import ModuleUsageChart from '../components/metrics/ModuleUsageChart';
import ModuleTrendsChart from '../components/metrics/ModuleTrendsChart';
import TopUsersTable from '../components/metrics/TopUsersTable';
import RecentActivityTable from '../components/metrics/RecentActivityTable';
import ModuleDetailCard from '../components/metrics/ModuleDetailCard';
import { 
    getKPISummary, 
    getModuleUsage, 
    getModuleTrends, 
    getTopUsers, 
    getRecentActivity,
    getModuleDetails 
} from '../services/metricsApi';

const HomePage = () => {
    const { t } = useLanguage();
    
    // Estados para datos
    const [kpiData, setKpiData] = useState(null);
    const [moduleUsageData, setModuleUsageData] = useState(null);
    const [trendsData, setTrendsData] = useState(null);
    const [topUsersData, setTopUsersData] = useState(null);
    const [recentActivityData, setRecentActivityData] = useState(null);
    const [policyDetails, setPolicyDetails] = useState(null);
    const [journalDetails, setJournalDetails] = useState(null);
    const [concursosDetails, setConcursosDetails] = useState(null);

    // Estados de carga
    const [loadingKPI, setLoadingKPI] = useState(true);
    const [loadingModuleUsage, setLoadingModuleUsage] = useState(true);
    const [loadingTrends, setLoadingTrends] = useState(true);
    const [loadingTopUsers, setLoadingTopUsers] = useState(true);
    const [loadingRecentActivity, setLoadingRecentActivity] = useState(true);
    const [loadingDetails, setLoadingDetails] = useState(true);

    // Estado de error
    const [error, setError] = useState(null);

    // Cargar datos al montar el componente
    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = useCallback(async () => {
        try {
            // Cargar todos los datos en paralelo para máxima velocidad
            const [kpi, usage, trends, topUsers, activity, policyData, journalData, concursosData] = await Promise.all([
                getKPISummary(),
                getModuleUsage(),
                getModuleTrends(30),
                getTopUsers(10),
                getRecentActivity(20),
                getModuleDetails('policy-navigator'),
                getModuleDetails('journal-finder'),
                getModuleDetails('concursos')
            ]);
            
            // Actualizar todos los estados
            setKpiData(kpi);
            setModuleUsageData(usage);
            setTrendsData(trends);
            setTopUsersData(topUsers);
            setRecentActivityData(activity);
            setPolicyDetails(policyData);
            setJournalDetails(journalData);
            setConcursosDetails(concursosData);
            
            // Actualizar estados de carga
            setLoadingKPI(false);
            setLoadingModuleUsage(false);
            setLoadingTrends(false);
            setLoadingTopUsers(false);
            setLoadingRecentActivity(false);
            setLoadingDetails(false);

        } catch (err) {
            console.error('Error cargando datos del dashboard:', err);
            setError(t('home.errorLoading'));
            // Detener todos los estados de carga
            setLoadingKPI(false);
            setLoadingModuleUsage(false);
            setLoadingTrends(false);
            setLoadingTopUsers(false);
            setLoadingRecentActivity(false);
            setLoadingDetails(false);
        }
    }, []);

    return (
        <div className="container-fluid px-0">
            {/* Encabezado */}
            <div className="row g-4 mb-4">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h1 className="h3 mb-1">
                                <i className="bi bi-speedometer2 me-2 text-primary"></i>
                                {t('home.title')}
                            </h1>
                            <p className="text-muted mb-0">{t('home.subtitle')}</p>
                        </div>
                        <button 
                            className="btn btn-outline-primary btn-sm"
                            onClick={loadDashboardData}
                            title={t('home.refresh')}
                        >
                            <i className="bi bi-arrow-clockwise me-1"></i>
                            {t('common.refresh')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mensaje de error */}
            {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {error}
                    <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setError(null)}
                    ></button>
                </div>
            )}

            {/* KPIs principales */}
            <div className="row g-4 mb-4">
                <div className="col-12">
                    <KPICards data={kpiData} loading={loadingKPI} />
                </div>
            </div>

            {/* Gráficos de uso y tendencias */}
            <div className="row g-4 mb-4">
                <div className="col-lg-6">
                    <ModuleUsageChart data={moduleUsageData} loading={loadingModuleUsage} />
                </div>
                <div className="col-lg-6">
                    <ModuleTrendsChart data={trendsData} loading={loadingTrends} days={30} />
                </div>
            </div>

            {/* Detalles por módulo */}
            <div className="row g-4 mb-4">
                <div className="col-lg-4">
                    <ModuleDetailCard 
                        moduleName={t('home.modules.policyNavigator.title')} 
                        data={policyDetails} 
                        loading={loadingDetails} 
                    />
                </div>
                <div className="col-lg-4">
                    <ModuleDetailCard 
                        moduleName={t('home.modules.journalFinder.title')} 
                        data={journalDetails} 
                        loading={loadingDetails} 
                    />
                </div>
                <div className="col-lg-4">
                    <ModuleDetailCard 
                        moduleName={t('home.modules.concursos.title')} 
                        data={concursosDetails} 
                        loading={loadingDetails} 
                    />
                </div>
            </div>

            {/* Top usuarios */}
            <div className="row g-4 mb-4">
                <div className="col-12">
                    <TopUsersTable data={topUsersData} loading={loadingTopUsers} />
                </div>
            </div>

            {/* Actividad reciente */}
            <div className="row g-4">
                <div className="col-12">
                    <RecentActivityTable data={recentActivityData} loading={loadingRecentActivity} />
                </div>
            </div>
        </div>
    );
};

export default HomePage;
